import React from "react";
import { Phone, Mail, MapPin, Clock, MessageSquare } from "lucide-react";
import SEO from "../Components/SEO";

const ContactUsPage = () => {
  const headOffice = {
    name: "Trademin Ltd.",
    address: "Ashburton, Canterbury",
    country: "New Zealand",
    phone: "+64 22 106 2796",
    email: "Franco.stander@trademin.co.nz",
    hours: {
      weekdays: "Monday – Friday: 7:00 AM – 6:00 PM",
      saturday: "Saturday: 8:00 AM – 2:00 PM",
      sunday: "Sunday: Emergency Calls Only",
    },
  };

  const contactMethods = [
    {
      icon: Phone,
      title: "Call Us",
      description: "Speak directly with our team for immediate assistance",
      action: headOffice.phone,
      link: `tel:${headOffice.phone.replace(/\s/g, "")}`,
      buttonText: "Call Now",
    },
    {
      icon: Mail,
      title: "Email Us",
      description:
        "Send us a detailed message and we'll respond within 24 hours",
      action: headOffice.email,
      link: `mailto:${headOffice.email}`,
      buttonText: "Send Email",
    },
    {
      icon: MessageSquare,
      title: "Request a Quote",
      description: "Get a detailed quote for your project requirements",
      action: "Free consultation included",
      link: "/request-quote",
      buttonText: "Get Quote",
    },
  ];

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');

    .cu-root { font-family: 'DM Sans', sans-serif; background: #fafaf8; }

    /* ── Hero ── */
    .cu-hero {
      background: #111; border-bottom: 1px solid rgba(255,255,255,0.06);
      padding: 10rem 7vw 7rem; position: relative; overflow: hidden;
    }
    .cu-hero-watermark {
      position: absolute; top: 50%; right: 5vw; transform: translateY(-50%);
      font-family: 'DM Serif Display', serif;
      font-size: clamp(6rem, 18vw, 16rem);
      color: rgba(255,255,255,0.025); line-height: 1;
      user-select: none; pointer-events: none; white-space: nowrap;
    }
    .cu-hero-glow {
      position: absolute; bottom: -30%; left: -10%;
      width: 60%; height: 80%;
      background: radial-gradient(circle, rgba(231,135,48,0.06) 0%, transparent 70%);
      pointer-events: none;
    }
    .cu-hero-content { position: relative; z-index: 1; max-width: 650px; }

    .cu-eyebrow { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 2rem; }
    .cu-eyebrow-line { width: 28px; height: 1px; background: #E78730; flex-shrink: 0; }
    .cu-eyebrow-text { font-size: 0.68rem; letter-spacing: 0.26em; text-transform: uppercase; color: #E78730; font-weight: 500; }

    .cu-hero h1 {
      font-family: 'DM Serif Display', serif;
      font-size: clamp(3rem, 7vw, 6rem); line-height: 0.95;
      color: #fff; margin-bottom: 2rem;
    }
    .cu-hero h1 em { color: #E78730; font-style: italic; }

    .cu-hero-desc { font-size: 0.95rem; color: rgba(255,255,255,0.38); line-height: 1.8; max-width: 48ch; font-weight: 300; margin-bottom: 3rem; }

    .cu-hero-btns { display: flex; gap: 1rem; flex-wrap: wrap; }
    .cu-btn-primary { display: inline-block; background: #E78730; color: #fff; font-size: 0.7rem; font-weight: 500; letter-spacing: 0.18em; text-transform: uppercase; text-decoration: none; padding: 0.9rem 2.25rem; font-family: 'DM Sans', sans-serif; transition: opacity 0.2s; }
    .cu-btn-primary:hover { opacity: 0.82; }
    .cu-btn-ghost { display: inline-block; border: 1px solid rgba(255,255,255,0.15); color: rgba(255,255,255,0.55); font-size: 0.7rem; font-weight: 400; letter-spacing: 0.18em; text-transform: uppercase; text-decoration: none; padding: 0.9rem 2.25rem; font-family: 'DM Sans', sans-serif; transition: border-color 0.25s, color 0.25s; }
    .cu-btn-ghost:hover { border-color: rgba(255,255,255,0.4); color: #fff; }

    /* ── Contact methods ── */
    .cu-methods {
      display: grid; grid-template-columns: repeat(3, 1fr);
      border-left: 1px solid #e8e8e4;
      border-top: 1px solid #e8e8e4;
    }

    .cu-method-card {
      padding: 3.5rem 3rem;
      border-right: 1px solid #e8e8e4;
      border-bottom: 1px solid #e8e8e4;
      display: flex; flex-direction: column;
      position: relative; overflow: hidden;
      transition: background 0.25s;
    }
    .cu-method-card:hover { background: #f4f1ec; }
    .cu-method-card::before {
      content: ''; position: absolute; top: 0; left: 0; right: 0;
      height: 2px; background: #E78730;
      transform: scaleX(0); transform-origin: left; transition: transform 0.3s;
    }
    .cu-method-card:hover::before { transform: scaleX(1); }

    .cu-method-icon { color: #E78730; margin-bottom: 2rem; }
    .cu-method-title { font-family: 'DM Serif Display', serif; font-size: 1.5rem; color: #1a1a1a; margin-bottom: 0.75rem; }
    .cu-method-desc { font-size: 0.85rem; color: #888; line-height: 1.75; font-weight: 300; margin-bottom: 1rem; flex: 1; }
    .cu-method-action { font-size: 0.8rem; color: #E78730; font-weight: 400; margin-bottom: 2rem; }

    .cu-method-btn {
      display: inline-flex; align-items: center; gap: 0.5rem;
      font-size: 0.68rem; letter-spacing: 0.16em; text-transform: uppercase;
      color: #1a1a1a; text-decoration: none; font-weight: 500;
      border-bottom: 1px solid #E78730; padding-bottom: 2px;
      width: fit-content; transition: color 0.2s, gap 0.2s;
    }
    .cu-method-btn:hover { color: #E78730; gap: 0.85rem; }
    .cu-method-btn svg { transition: transform 0.25s; }
    .cu-method-btn:hover svg { transform: translateX(3px); }

    /* ── Info + map split ── */
    .cu-info-section {
      display: grid; grid-template-columns: 1fr 1fr;
      border-top: 1px solid #e8e8e4;
      border-bottom: 1px solid #e8e8e4;
    }

    .cu-info-pane {
      padding: 6rem 7vw;
      border-right: 1px solid #e8e8e4;
    }

    .cu-section-eyebrow { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1.5rem; }
    .cu-section-eyebrow-line { width: 28px; height: 1px; background: #E78730; flex-shrink: 0; }
    .cu-section-eyebrow-text { font-size: 0.68rem; letter-spacing: 0.26em; text-transform: uppercase; color: #E78730; font-weight: 500; }

    .cu-info-h2 { font-family: 'DM Serif Display', serif; font-size: clamp(1.75rem, 3vw, 2.5rem); color: #1a1a1a; line-height: 1.05; margin-bottom: 3rem; }
    .cu-info-h2 em { color: #E78730; font-style: italic; }

    .cu-info-row {
      display: flex; align-items: flex-start; gap: 1.5rem;
      padding: 1.75rem 0; border-bottom: 1px solid #e8e8e4;
    }
    .cu-info-row:first-of-type { padding-top: 0; }
    .cu-info-row:last-child { border-bottom: none; }

    .cu-info-icon { color: #E78730; flex-shrink: 0; margin-top: 2px; }

    .cu-info-row-label {
      font-size: 0.62rem; letter-spacing: 0.2em; text-transform: uppercase;
      color: #bbb; font-weight: 500; margin-bottom: 0.5rem;
    }
    .cu-info-row-value { font-size: 0.9rem; color: #1a1a1a; font-weight: 300; line-height: 1.7; }
    .cu-info-row-value a { color: #1a1a1a; text-decoration: none; transition: color 0.2s; }
    .cu-info-row-value a:hover { color: #E78730; }

    .cu-hours-item { margin-bottom: 0.4rem; }
    .cu-hours-day { color: #555; font-weight: 400; }

    /* Map pane */
    .cu-map-pane {
      background: #ede9e3;
      display: flex; align-items: center; justify-content: center;
      flex-direction: column; gap: 1rem; min-height: 500px;
    }
    .cu-map-icon { color: #c8c0b4; }
    .cu-map-label { font-size: 0.65rem; letter-spacing: 0.2em; text-transform: uppercase; color: #c8c0b4; }

    /* ── Responsive ── */
    @media (max-width: 900px) {
      .cu-methods { grid-template-columns: 1fr; }
      .cu-method-card { border-right: none; }
      .cu-info-section { grid-template-columns: 1fr; }
      .cu-info-pane { border-right: none; border-bottom: 1px solid #e8e8e4; padding: 4rem 7vw; }
      .cu-map-pane { min-height: 300px; }
    }

    @media (max-width: 640px) {
      .cu-hero { padding: 8rem 7vw 5rem; }
    }
  `;

  return (
    <>
      <SEO
        title="Contact Us | Trademin Ltd"
        description="Get in touch with Trademin Ltd for electrical and mechanical services in Ashburton and Canterbury."
        canonical="https://www.trademin.co.nz/contact-us"
        ogTitle="Contact Us | Trademin Ltd"
        ogDescription="Contact Trademin Ltd for professional electrical and mechanical services across Canterbury."
      />
      <style>{styles}</style>

      <div className="cu-root">
        {/* Hero */}
        <section className="cu-hero">
          <div className="cu-hero-glow" />
          <div className="cu-hero-watermark">CONTACT</div>
          <div className="cu-hero-content">
            <div className="cu-eyebrow">
              <span className="cu-eyebrow-line" />
              <span className="cu-eyebrow-text">We're Here to Help</span>
            </div>
            <h1>
              Get In <em>Touch</em>
            </h1>
            <p className="cu-hero-desc">
              Reach out to our experienced engineering team. We're available
              Monday to Friday and ready to help with any project.
            </p>
            <div className="cu-hero-btns">
              <a
                href={`tel:${headOffice.phone.replace(/\s/g, "")}`}
                className="cu-btn-primary"
              >
                Call Us Now
              </a>
              <a href={`mailto:${headOffice.email}`} className="cu-btn-ghost">
                Send Email
              </a>
            </div>
          </div>
        </section>

        {/* Contact methods */}
        <div className="cu-methods">
          {contactMethods.map((method, index) => (
            <div key={index} className="cu-method-card">
              <method.icon
                size={22}
                strokeWidth={1.25}
                className="cu-method-icon"
              />
              <h3 className="cu-method-title">{method.title}</h3>
              <p className="cu-method-desc">{method.description}</p>
              <p className="cu-method-action">{method.action}</p>
              <a href={method.link} className="cu-method-btn">
                {method.buttonText}
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </a>
            </div>
          ))}
        </div>

        {/* Info + Map */}
        <div className="cu-info-section">
          <div className="cu-info-pane">
            <div className="cu-section-eyebrow">
              <span className="cu-section-eyebrow-line" />
              <span className="cu-section-eyebrow-text">Our Location</span>
            </div>
            <h2 className="cu-info-h2">
              Visit Our <em>Head Office</em>
            </h2>

            <div className="cu-info-row">
              <MapPin size={16} strokeWidth={1.5} className="cu-info-icon" />
              <div>
                <div className="cu-info-row-label">Address</div>
                <div className="cu-info-row-value">
                  {headOffice.name}
                  <br />
                  {headOffice.address}
                  <br />
                  {headOffice.country}
                </div>
              </div>
            </div>

            <div className="cu-info-row">
              <Phone size={16} strokeWidth={1.5} className="cu-info-icon" />
              <div>
                <div className="cu-info-row-label">Phone</div>
                <div className="cu-info-row-value">
                  <a href={`tel:${headOffice.phone.replace(/\s/g, "")}`}>
                    {headOffice.phone}
                  </a>
                </div>
              </div>
            </div>

            <div className="cu-info-row">
              <Mail size={16} strokeWidth={1.5} className="cu-info-icon" />
              <div>
                <div className="cu-info-row-label">Email</div>
                <div className="cu-info-row-value">
                  <a href={`mailto:${headOffice.email}`}>{headOffice.email}</a>
                </div>
              </div>
            </div>

            <div className="cu-info-row">
              <Clock size={16} strokeWidth={1.5} className="cu-info-icon" />
              <div>
                <div className="cu-info-row-label">Business Hours</div>
                <div className="cu-info-row-value">
                  <div className="cu-hours-item">
                    <span className="cu-hours-day">Mon – Fri</span> &nbsp;7:00
                    AM – 6:00 PM
                  </div>
                  <div className="cu-hours-item">
                    <span className="cu-hours-day">Saturday</span> &nbsp;8:00 AM
                    – 2:00 PM
                  </div>
                  <div className="cu-hours-item">
                    <span className="cu-hours-day">Sunday</span> &nbsp;Emergency
                    Calls Only
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="cu-map-pane">
            <MapPin size={40} strokeWidth={1} className="cu-map-icon" />
            <span className="cu-map-label">Interactive Map — Coming Soon</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactUsPage;
